import * as React from 'react';
import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useContext,
  createContext,
} from 'react';
import {view, tokens, createView} from '@sail/ui';
import {createKeyframes} from '@sail/engine';
import {defineMessages, useIntl} from 'react-intl';

const shimmer = createKeyframes({
  0: {backgroundPosition: '200% center'},
  100: {backgroundPosition: '-200% center'},
});

const messages = defineMessages({
  in_progress: {
    id: 'conversations_ui.loading_text.in_progress',
    description:
      'The second loading text that shows when the first one is done',
    defaultMessage: 'In progress',
  },
  generating_response: {
    id: 'conversations_ui.loading_text.generating_response',
    description:
      'The third loading text that shows when the second one is done',
    defaultMessage: 'Generating response',
  },
});

type LoadingStage = 'progress' | 'generating';

const LoadingTextContext = createContext({
  loadingStage: 'progress',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setLoadingStage: (_: LoadingStage) => {},
});

export function LoadingTextProvider({children}: {children: React.ReactNode}) {
  const [loadingStage, setLoadingStage] = useState('progress');
  const context = useMemo(
    () => ({loadingStage, setLoadingStage}),
    [loadingStage],
  );

  return (
    <LoadingTextContext.Provider value={context}>
      {children}
    </LoadingTextContext.Provider>
  );
}

interface LoadingTextProps {
  shouldReset?: boolean;
  customText?: string;
}

export const LoadingText = createView<LoadingTextProps>(
  ({shouldReset, customText, ...props}) => {
    const intl = useIntl();
    const [dots, setDots] = useState('');

    const {loadingStage, setLoadingStage} = useContext(LoadingTextContext);
    const shouldResetRef = useRef(shouldReset);
    const [opacity, setOpacity] = useState(1);

    const content = useMemo(() => {
      if (customText) {
        return customText;
      }

      switch (loadingStage) {
        case 'progress':
          return intl.formatMessage(messages.in_progress);
        case 'generating':
          return intl.formatMessage(messages.generating_response);
        default:
          return '';
      }
    }, [intl, loadingStage, customText]);

    useLayoutEffect(() => {
      if (shouldResetRef.current) {
        setLoadingStage('progress');
        shouldResetRef.current = false;
      }
    }, [setLoadingStage]);

    useEffect(() => {
      const interval = setInterval(() => {
        if (dots.length === 3) {
          setDots('');
        } else {
          setDots((prev) => `${prev}.`);
        }
      }, 400);

      return () => clearInterval(interval);
    });

    useEffect(() => {
      const updateContent = (stage: LoadingStage) => {
        setOpacity(0);
        setTimeout(() => {
          setLoadingStage(stage);
          setOpacity(1);
        }, 150);
      };

      let timer: ReturnType<typeof setTimeout> | undefined;
      switch (loadingStage) {
        case 'progress':
          timer = setTimeout(() => {
            updateContent('generating');
            setDots('');
          }, 3000);
          break;
        default:
        // We are already at the last stage, do nothing
      }

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }, [intl, loadingStage, setLoadingStage]);

    return (
      <view.div data-testid="loader" {...props}>
        <view.span
          css={{
            animation: `${shimmer} 2s linear infinite`,
            fontSize: 'label.medium',
            background: `linear-gradient(90deg, ${tokens.color.neutral[300].toString()}, ${tokens.color.neutral[500].toString()}, ${tokens.color.text.subdued.toString()}, ${tokens.color.text.$default.toString()}, ${tokens.color.neutral[300].toString()})`,
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            color: 'transparent',
            opacity,
            transition: 'opacity 0.15s ease-in-out',
          }}
        >
          {content}
          {dots}
        </view.span>
      </view.div>
    );
  },
);
